import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { Phone, MapPin, Menu, X } from "lucide-react";
import { BUSINESS } from "../data/site";

const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/products", label: "Products" },
    { to: "/about", label: "About" },
    { to: "/reviews", label: "Reviews" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "glass premium-shadow" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
                    <div className="h-9 w-9 rounded-xl bg-slate-900 grid place-items-center text-white font-display font-bold">EG</div>
                    <div className="leading-tight">
                        <div className="font-display font-bold text-slate-900 tracking-tight">EG Wireless</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Glendale, CA</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-3.5 py-2 text-sm font-medium rounded-full transition-colors ${
                                    isActive
                                        ? "text-slate-900 bg-slate-100"
                                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    <a
                        href={BUSINESS.mapsDirections}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="nav-directions-btn"
                        className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 transition"
                    >
                        <MapPin className="w-4 h-4" /> Directions
                    </a>
                    <a
                        href={`tel:${BUSINESS.phoneRaw}`}
                        data-testid="nav-call-btn"
                        className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition"
                    >
                        <Phone className="w-4 h-4" /> {BUSINESS.phone}
                    </a>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    className="lg:hidden h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden glass border-t border-slate-200/60">
                    <div className="px-5 py-4 flex flex-col gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `px-4 py-3 rounded-xl text-sm font-medium ${
                                        isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <a
                            href={`tel:${BUSINESS.phoneRaw}`}
                            data-testid="mobile-call-btn"
                            className="mt-2 inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-slate-900 text-white font-semibold"
                        >
                            <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
